/**
 * Pool Card Component
 * VOY-11: Displays a competition pool tier
 */

import { Button } from '@/components/ui/button';
import { Plane, Users, Clock, Coins } from 'lucide-react';
import { GamePool, getTierColor, getTierEmoji } from '@/data/gaming-mockup';

interface PoolCardProps {
  pool: GamePool;
  onJoin: () => void;
}

export function PoolCard({ pool, onJoin }: PoolCardProps) {
  const tierStyle = getTierColor(pool.tier);
  const tierEmoji = getTierEmoji(pool.tier);
  const progress = (pool.currentPlayers / pool.requiredPlayers) * 100;
  
  const getStatusBadge = () => {
    switch (pool.status) {
      case 'active':
        return <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">🟢 Active</span>;
      case 'almost-full':
        return <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full animate-pulse">⚡ Almost Full!</span>;
      case 'filling':
        return <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full">🟡 Filling Up</span>;
    }
  };

  return (
    <div className="bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
      {/* Tier Header */}
      <div className={`bg-gradient-to-r ${tierStyle.gradient} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-3xl">{tierEmoji}</span>
            <div>
              <h3 className="text-white font-bold text-xl uppercase">
                {pool.tier} Pool
              </h3>
              <p className="text-white/80 text-sm">
                Max Trip Value: ${pool.maxTripValue.toLocaleString()}
              </p>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Destination */}
        <div className="flex items-center gap-2 text-gray-700 dark:text-white">
          <Plane className="w-5 h-5 text-[#5B6FED] dark:text-blue-400" />
          <span className="font-semibold">
            {pool.destination} ({pool.destinationCode})
          </span>
        </div>

        {/* Prizes */}
        <div>
          <p className="text-sm font-semibold text-gray-700 dark:text-white mb-2">Prizes:</p>
          <div className="space-y-1">
            {pool.prizes.map((prize, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-zinc-300">
                  {prize.rank}: {prize.description}
                </span>
                <span className="font-semibold text-[#5B6FED] dark:text-blue-400">
                  ${prize.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Player Progress */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="flex items-center gap-1 text-gray-600 dark:text-zinc-300">
              <Users className="w-4 h-4" />
              Players
            </span>
            <span className="font-semibold dark:text-white">
              {pool.currentPlayers.toLocaleString()} / {pool.requiredPlayers.toLocaleString()}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-zinc-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${tierStyle.gradient} transition-all duration-500 ease-out`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1 text-center">
            {progress.toFixed(1)}% filled
          </p>
        </div>

        {/* Time Remaining */}
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-300">
          <Clock className="w-4 h-4" />
          <span>Time remaining: <span className="font-semibold">{pool.timeRemaining}</span></span>
        </div>

        {/* Entry Fee */}
        <div className="bg-blue-50 dark:bg-zinc-700/50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-white">Entry Fee</p>
              <p className="text-xs text-gray-500 dark:text-zinc-400">Choose payment method</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg text-[#5B6FED] dark:text-blue-400">${pool.entryFee}</p>
              <p className="text-xs text-gray-500 dark:text-zinc-400 flex items-center gap-1 justify-end">
                or <Coins className="w-3 h-3" /> {pool.entryFeeCoins} coins
              </p>
            </div>
          </div>
        </div>

        {/* Join Button */}
        <Button
          onClick={onJoin}
          className={`w-full rounded-full bg-gradient-to-r ${tierStyle.gradient} hover:opacity-90 text-white font-semibold py-6 text-lg shadow-lg`}
        >
          Join {pool.tier.charAt(0).toUpperCase() + pool.tier.slice(1)} Pool
        </Button>
      </div>
    </div>
  );
}

